import { JsonApiRequestError } from '@farfetched/core';
import { ComboboxItem } from '@mantine/core';
import { combine, createStore, sample, Store } from 'effector';
import { debounce } from 'patronum';
import {
	createGetCashdeskAllQuery,
	createGetDeviceAllQuery,
	createGetGamesQuery,
	createPlayerRegistryQuery,
	PlayerRegistryFilters,
	PlayerRegistryItem,
} from 'shared/api';
import { createRegistryModel, RegistryCallbacks, RegistryModel, SORT_DIRECTIONS } from 'shared/lib';

type CreatePlayerRegistryModelParams = {
	$currencies: Store<string[]>;
	$accountId?: Store<number | null>;
	$cashdeskId?: Store<number | null>;
	$allAccountIds?: Store<number[]>;
} & RegistryCallbacks;

export type PlayerRegistryModel = {
	model: RegistryModel<PlayerRegistryItem, PlayerRegistryFilters, JsonApiRequestError>;
	$games: Store<ComboboxItem[]>;
	$cashdesks: Store<ComboboxItem[]>;
	$devices: Store<ComboboxItem[]>;
	$currencies: Store<string[]>;
};

export const createPlayerRegistryModel = ({
	$accountId,
	$cashdeskId,
	$currencies,
	$allAccountIds,
	...params
}: CreatePlayerRegistryModelParams): PlayerRegistryModel => {
	const query = createPlayerRegistryQuery($accountId, $cashdeskId);

	const getGamesQuery = createGetGamesQuery();

	const getCashdeskAllQuery = createGetCashdeskAllQuery();

	const getDeviceAllQuery = createGetDeviceAllQuery();

	const model = createRegistryModel({
		query,
		filters: {
			name: '',
			accountIds: [],
			isConnected: [],
			currencies: [],
			deviceIds: [],
			gameIds: [],
			cashdeskIds: [],
		},
		sortField: 'name',
		sortDirection: SORT_DIRECTIONS.ASC,
		limit: 10,
		...params,
	});

	const $games = getGamesQuery.$data.map(
		(games) => games?.map(({ id, title }) => ({ value: `${id}`, label: title })) ?? [],
	);

	const $cashdesks = getCashdeskAllQuery.$data.map(
		(cashdesks) => cashdesks?.map(({ id, name }) => ({ value: `${id}`, label: name })) ?? [],
	);

	const $devices = getDeviceAllQuery.$data.map(
		(devices) => devices?.map(({ id, name }) => ({ value: `${id}`, label: name })) ?? [],
	);

	const $filterAccountIds = combine(
		model.filters.accountIds.$value,
		$allAccountIds ?? createStore<number[]>([]),
		(selected, all) => (selected.length > 0 ? selected : all),
	);

	sample({
		clock: debounce($filterAccountIds, 300),
		source: model.gate.status,
		filter: (isOpen, ids) => isOpen && ids.length > 0,
		fn: (_, ids) => ids,
		target: [getCashdeskAllQuery.start, getDeviceAllQuery.start],
	});

	sample({
		clock: getCashdeskAllQuery.finished.success,
		source: { all: $cashdesks, current: model.filters.cashdeskIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashdesk) => current.includes(Number(cashdesk.value)))
				.map((cashdesk) => Number(cashdesk.value)),
		target: model.filters.cashdeskIds.setValue,
	});

	sample({
		clock: getDeviceAllQuery.finished.success,
		source: { all: $devices, current: model.filters.deviceIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((device) => current.includes(Number(device.value)))
				.map((device) => Number(device.value)),
		target: model.filters.deviceIds.setValue,
	});

	sample({
		clock: model.gate.open,
		source: $filterAccountIds,
		filter: (ids) => ids.length > 0,
		target: [getCashdeskAllQuery.start, getDeviceAllQuery.start],
	});

	sample({
		clock: model.gate.open,
		target: getGamesQuery.start,
	});

	sample({
		clock: model.gate.close,
		target: [getCashdeskAllQuery.reset, getDeviceAllQuery.reset, getGamesQuery.reset],
	});

	return { model, $games, $cashdesks, $devices, $currencies };
};
